/**
 * =============================================================================
 * SHARY SEASONAL PRESETS — Configuration Module
 * =============================================================================
 * Defines visual presets for each season following Shary Gold Visual Spec.
 * 
 * PHILOSOPHY: Luxury through restraint.
 * - Cinematic, calm, intentional, expensive
 * - Motion speed: 12s–28s cycles
 * - Easing: ease-in-out only
 * - No jitter, no bounce
 * 
 * DENSITY LIMITS (HARD CAPS):
 * - Mobile: 8–14 elements
 * - Tablet: 12–20 elements
 * - Desktop: 16–28 elements
 * =============================================================================
 */

(function() {
  'use strict';

  /**
   * SVG Icons for toggle button (season-specific)
   */
  const ICONS = {
    // Snowflake for winter
    winter: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83M12 8a4 4 0 100 8 4 4 0 000-8z" stroke="currentColor" stroke-width="1.5" fill="none"/></svg>`,
    
    // Lantern for Ramadan
    ramadan: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2 4h-4l2-4zm-3 5h6v2c0 1-1 2-1 3v6c0 1-1 2-2 2s-2-1-2-2v-6c0-1-1-2-1-3V7z" stroke="currentColor" stroke-width="1" fill="none"/><circle cx="12" cy="13" r="2" fill="currentColor" opacity="0.3"/></svg>`,
    
    // Star for Eid
    eid: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.4 7.2H22l-6 4.8 2.4 7.2L12 16.8 5.6 21.2 8 14 2 9.2h7.6L12 2z" stroke="currentColor" stroke-width="1" fill="none"/></svg>`,
    
    // Sun for summer
    summer: `<svg viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="5" stroke="currentColor" stroke-width="1.5" fill="none"/><path d="M12 1v3m0 16v3M4.22 4.22l2.12 2.12m11.32 11.32l2.12 2.12M1 12h3m16 0h3M4.22 19.78l2.12-2.12m11.32-11.32l2.12-2.12" stroke="currentColor" stroke-width="1.5"/></svg>`,
    
    // Tag for Black Friday
    blackfriday: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" stroke="currentColor" stroke-width="1.5" fill="none"/><circle cx="7" cy="7" r="1.5" fill="currentColor"/></svg>`,
    
    // Default sparkle
    default: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 3v18M3 12h18M5.64 5.64l12.72 12.72M18.36 5.64L5.64 18.36" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>`
  };

  /**
   * Density limits per device tier
   */
  const DENSITY = {
    mobile: { min: 8, max: 14 },
    tablet: { min: 12, max: 20 },
    desktop: { min: 16, max: 28 }
  };

  /**
   * Season Presets — Shary Gold Visual Spec Compliant
   */
  const PRESETS = {
    
    /**
     * WINTER — Quiet winter morning
     * Two layers of snow: background (slow, small) and foreground (diagonal drift)
     */
    winter: {
      id: 'winter',
      label: 'شتاء',
      icon: ICONS.winter,
      
      // Element creation
      layers: [
        {
          type: 'particle',
          layer: 'bg',
          count: { mobile: 5, tablet: 8, desktop: 12 },
          element: 'snowflake'
        },
        {
          type: 'particle',
          layer: 'fg',
          count: { mobile: 4, tablet: 6, desktop: 10 },
          element: 'snowflake'
        }
      ],
      
      // Animation timing (slow, cinematic)
      timing: {
        bgDuration: 24000,  // 24s
        fgDuration: 18000,  // 18s
        spawnDelay: 400     // Gradual spawn
      },
      
      // Style parameters
      style: {
        bgOpacity: 0.05,
        fgOpacity: 0.12,
        bgSize: 4,
        fgSize: 6
      }
    },

    /**
     * RAMADAN — Spiritual, warm, respectful
     * Floating fanoos silhouettes with soft radial glow halos
     */
    ramadan: {
      id: 'ramadan',
      label: 'رمضان',
      icon: ICONS.ramadan,
      
      layers: [
        {
          type: 'lantern',
          layer: 'bg',
          count: { mobile: 3, tablet: 4, desktop: 6 },
          element: 'lantern'
        },
        {
          type: 'lantern',
          layer: 'fg',
          count: { mobile: 3, tablet: 5, desktop: 8 },
          element: 'lantern'
        }
      ],
      
      timing: {
        bgDuration: 26000,  // 26s
        fgDuration: 22000,  // 22s
        spawnDelay: 600
      },
      
      style: {
        bgOpacity: 0.08,
        fgOpacity: 0.12,
        bgSize: { width: 18, height: 30 },
        fgSize: { width: 28, height: 46 },
        glowOpacity: 0.08,
        swayAmount: 6       // Max 6px side sway
      }
    },

    /**
     * EID — Celebration with control
     * First load: 2-3s gold confetti intro, then subtle sparkles only
     */
    eid: {
      id: 'eid',
      label: 'عيد',
      icon: ICONS.eid,
      
      // Confetti intro (first load only)
      intro: {
        enabled: true,
        duration: 2500,    // 2.5s
        confettiCount: { mobile: 8, tablet: 12, desktop: 18 },
        sessionKey: 'shary_eid_intro_shown_v1'
      },
      
      // Persistent sparkles
      layers: [
        {
          type: 'sparkle',
          layer: 'bg',
          count: { mobile: 4, tablet: 6, desktop: 10 },
          element: 'sparkle'
        },
        {
          type: 'sparkle',
          layer: 'fg',
          count: { mobile: 3, tablet: 5, desktop: 8 },
          element: 'sparkle'
        }
      ],
      
      timing: {
        bgDuration: 8000,   // 8s
        fgDuration: 5000,   // 5s
        spawnDelay: 300
      },
      
      style: {
        bgOpacity: 0.06,
        fgOpacity: 0.10,
        bgSize: 3,
        fgSize: 5,
        confettiOpacity: 0.16
      }
    },

    /**
     * SUMMER — Heat, light, calm power
     * NO particles. Moving gradient light rays and soft shimmer sweep.
     */
    summer: {
      id: 'summer',
      label: 'صيف',
      icon: ICONS.summer,
      
      layers: [
        {
          type: 'ray',
          count: { mobile: 1, tablet: 2, desktop: 2 },
          element: 'ray'
        },
        {
          type: 'shimmer',
          count: { mobile: 1, tablet: 1, desktop: 1 },
          element: 'shimmer'
        }
      ],
      
      timing: {
        rayDuration: 28000,   // 28s
        shimmerDuration: 20000 // 20s
      },
      
      style: {
        rayOpacity: 0.05,
        shimmerOpacity: 0.04
      }
    },

    /**
     * BLACK FRIDAY — Power, urgency, control
     * Dark subtle overlay gradient with slow orange light streaks
     */
    blackfriday: {
      id: 'blackfriday',
      label: 'جمعة بيضاء',
      icon: ICONS.blackfriday,
      
      layers: [
        {
          type: 'streak',
          count: { mobile: 4, tablet: 6, desktop: 8 },
          element: 'streak'
        }
      ],
      
      timing: {
        streakDuration: 16000  // 16s
      },
      
      style: {
        streakOpacity: 0.12,
        overlayOpacity: 0.03
      }
    }
  };

  /**
   * Device tier detection
   * @returns {'mobile' | 'tablet' | 'desktop'}
   */
  function getDeviceTier() {
    const width = window.innerWidth;
    if (width < 768) return 'mobile';
    if (width < 1024) return 'tablet';
    return 'desktop';
  }

  /**
   * Get particle count for device
   * @param {Object} countConfig - { mobile, tablet, desktop }
   * @returns {number}
   */
  function getCountForDevice(countConfig) {
    const tier = getDeviceTier();
    return countConfig[tier] || countConfig.mobile;
  }

  /**
   * Check if device is low-end (for intensity forcing)
   * @returns {boolean}
   */
  function isLowEndDevice() {
    // Check for hardware concurrency (CPU cores)
    if (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 2) {
      return true;
    }
    
    // Check device memory API (if available)
    if (navigator.deviceMemory && navigator.deviceMemory <= 2) {
      return true;
    }
    
    // Check for slow connection
    if (navigator.connection) {
      const { effectiveType, saveData } = navigator.connection;
      if (saveData || effectiveType === 'slow-2g' || effectiveType === '2g') {
        return true;
      }
    }
    
    return false;
  }

  /**
   * Determine appropriate intensity
   * @param {number} settingValue - User setting (1-3)
   * @returns {number} 1, 2, or 3
   */
  function determineIntensity(settingValue) {
    // Low-end devices always get intensity 1
    if (isLowEndDevice()) {
      return 1;
    }
    
    // Respect user setting
    return Math.max(1, Math.min(3, settingValue || 2));
  }

  /**
   * Validate total element count against density limits
   * @param {Object} preset - Season preset
   * @returns {boolean}
   */
  function validateDensity(preset) {
    const tier = getDeviceTier();
    const limits = DENSITY[tier];
    
    let totalCount = 0;
    
    if (preset.layers) {
      preset.layers.forEach(layer => {
        totalCount += getCountForDevice(layer.count);
      });
    }
    
    if (preset.intro && preset.intro.enabled) {
      totalCount += getCountForDevice(preset.intro.confettiCount || { mobile: 0, tablet: 0, desktop: 0 });
    }
    
    return totalCount <= limits.max;
  }

  // Export to global namespace
  window.SharySeasonalPresets = {
    PRESETS,
    ICONS,
    DENSITY,
    getDeviceTier,
    getCountForDevice,
    isLowEndDevice,
    determineIntensity,
    validateDensity
  };

})();
